import type { OptionsType } from '@openx/types/options';
import type { SelectRateFormState } from '@openx/types/targeting/selectRate';
import type { TargetingReadOnlyProps } from '@openx/types/targeting/targeting';
import { useTargetingDisabledState } from '../../../utils/hooks/useTargetingDisabledState';
import { useDropdownOpen } from '../ReadOnlyTargetingDropdown/hooks/useDropdownOpen';
import { StyledReadOnlyTargetingDropdown } from '../ReadOnlyTargetingDropdown/ReadOnlyTargetingDropdown';
import { TargetingReadOnly } from '../TargetingReadOnly/TargetingReadOnly';
import { FormPreviewItems } from './FormPreviewItems';
import { Items } from './Items';

interface SelectRateReadOnlyProps extends TargetingReadOnlyProps<SelectRateFormState> {
  filterTitle: string;
  dataTest?: string;
  optionsFetch: typeof OptionsType.VIEWABILITY | typeof OptionsType.VIEW_THROUGH_RATE;
  description?: string;
  field: string;
  userChangedInitialValue: boolean;
}

export const SelectRateReadOnly = ({
  name,
  field,
  userChangedInitialValue,
  onOpenDrawerClick,
  onRemoveClick,
  targetingParams,
  readonly,
  optionsFetch,
  filterTitle,
  dataTest,
  description,
}: SelectRateReadOnlyProps) => {
  const { isDisabledOrHasError, disabledReasonMessage, errorReasonMessage } = useTargetingDisabledState(field);

  const isEmpty = !targetingParams?.val;
  const optionsCount = targetingParams?.val ? 1 : 0;
  const hasValue = !!optionsCount;
  const { isOpen, toggleOpen } = useDropdownOpen(hasValue);

  if (!readonly)
    return (
      <TargetingReadOnly
        name={name}
        field={field}
        userChangedInitialValue={userChangedInitialValue}
        isEmpty={isEmpty}
        onOpenDrawerClick={onOpenDrawerClick}
        onRemove={onRemoveClick}
        readonly={readonly}
        isDisabled={isDisabledOrHasError}
        disabledReasonMessage={disabledReasonMessage}
        description={description}
        isSingleChoice
        optionsCount={optionsCount}
        isOpen={isOpen}
        toggleOpen={toggleOpen}
      >
        {!isEmpty && isOpen && (
          <StyledReadOnlyTargetingDropdown>
            <FormPreviewItems
              targetingParams={targetingParams as SelectRateFormState}
              optionsFetch={optionsFetch}
              title={filterTitle}
              dataTest={dataTest}
              onOpenDrawerClick={onOpenDrawerClick}
              onRemove={onRemoveClick}
              isDisabled={isDisabledOrHasError}
              disabledReasonMessage={errorReasonMessage}
            />
          </StyledReadOnlyTargetingDropdown>
        )}
      </TargetingReadOnly>
    );

  return (
    <TargetingReadOnly
      name={name}
      field={field}
      userChangedInitialValue={userChangedInitialValue}
      isEmpty={isEmpty}
      onOpenDrawerClick={onOpenDrawerClick}
      onRemove={onRemoveClick}
      readonly={readonly}
      isDisabled={isDisabledOrHasError}
      disabledReasonMessage={disabledReasonMessage}
      description={description}
    >
      {!isEmpty && (
        <Items
          targetingParams={targetingParams as SelectRateFormState}
          optionsFetch={optionsFetch}
          title={filterTitle}
          dataTest={dataTest}
          disabledReasonMessage={errorReasonMessage}
        />
      )}
    </TargetingReadOnly>
  );
};
